import React from "react";

import { Link } from "react-router-dom";

import { ReactComponent as ArrowDownBlack } from "commons/assets/icons/arrow_down_black.svg";
import { ReactComponent as ArrowRight } from "commons/assets/icons/arrow_right.svg";

import { useMenuOpenActionsContext } from "commons/context/menu";

import useMatchLocation from "commons/hooks/useMatchLocation";

import { MenuType } from "commons/types/menu";

export type PropsType = {
    subMenu: MenuType;
    menuVersion?: string;
};

function SubMenuItem({ subMenu, menuVersion }: PropsType) {
    const { toggleMenuWhenClose } = useMenuOpenActionsContext();

    const match = useMatchLocation(subMenu.path);

    const ariaExpanded: boolean | undefined = React.useMemo(() => {
        if (subMenu.children.length > 0) {
            return match;
        }

        return undefined;
    }, [subMenu.children.length, match]);

    const ariaControls: string | undefined = React.useMemo(() => {
        if (subMenu.children.length > 0) {
            return `sub-menu-children-${subMenu.label.toLowerCase().replace(/\s/g, "")}`;
        }

        return undefined;
    }, [subMenu.children.length, subMenu.label]);

    return (
        <div
            className={`sub-item ${match ? " active " : ""} ${
                subMenu.children.length > 0 ? " with-arrow " : ""
            }`}
            data-testid="submenu-item"
        >
            <div className={"sub-menu-item"}>
                {subMenu.labelElement}

                {!subMenu.labelElement && (
                    <>
                        {subMenu.children.length > 0 && (
                            <ArrowDownBlack
                                className={`arrow-down ${match ? " active " : ""}`}
                                data-testid="arrow-down"
                            />
                        )}

                        <Link
                            id={`sub-menu-children-${subMenu.label
                                .toLowerCase()
                                .replace(/\s/g, "")}`}
                            to={subMenu.path}
                            onClick={
                                subMenu.children.length === 0 ? toggleMenuWhenClose : undefined
                            }
                            data-testid={"menu-item-link"}
                            aria-current={match ? "page" : undefined}
                            aria-expanded={ariaExpanded}
                            aria-controls={ariaControls}
                        >
                            {menuVersion === "v2" && (
                                <div
                                    className={`sub-menu-children-dot ${match ? " active " : ""}`}
                                />
                            )}

                            {subMenu.children.length > 0 && (
                                <ArrowRight
                                    className={`arrow-right ${!match ? " active " : ""}`}
                                    data-testid="arrow-right"
                                />
                            )}

                            <div className={"label-icon"} data-testid={"submenu-item-link"}>
                                <p lang={subMenu.lang}>{subMenu.label}</p>
                                {subMenu.icon ?? null}
                            </div>
                        </Link>
                    </>
                )}

                {subMenu.notificationBadge ?? null}
            </div>

            {subMenu.children.length > 0 && (
                <div
                    id={`sub-menu-children-${subMenu.label.toLowerCase().replace(/\s/g, "")}`}
                    role={"menu"}
                    aria-labelledby={`sub-menu-children-${subMenu.label
                        .toLowerCase()
                        .replace(/\s/g, "")}`}
                    className={"submenu-item-children"}
                >
                    {subMenu.children.map((child) => {
                        return <SubMenuItem key={child.path} subMenu={child} />;
                    })}
                </div>
            )}
        </div>
    );
}

export default SubMenuItem;
